/**
 * DealPeopleSection - Seção de pessoas relacionadas ao negócio
 * Exibe criador, responsável e vendedor do deal
 * 
 * Versão: 1.3.0
 * - v1.3.0: Ícone de lápis opcional (prop onEdit) em Responsável e Vendedor
 * - Layout "Criado por" horizontal compacto no topo
 * - Responsável e Vendedor em cards com indentação e background sutil
 */
import React from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Users, Briefcase, UserCircle, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SmartAvatar } from '@/components/ui/smart-avatar';

// Interface para pessoa genérica
interface PersonInfo {
  id: string;
  name?: string | null;
  full_name?: string | null;
  email?: string | null;
  avatar_url?: string | null;
}

// Props do componente
interface DealPeopleSectionProps {
  seller: PersonInfo | null;
  assignedUser: PersonInfo | null;
  createdByUser: PersonInfo | null;
  createdAt: string;
  /** v1.3.0: abre o modal de edição de Responsável/Vendedor (só quando permitido) */
  onEditPeople?: () => void;
}

/**
 * Linha compacta horizontal para "Criado por"
 * Ícone + Label à esquerda, Avatar + Nome + Data à direita
 */
function CreatedByRow({
  person,
  createdAt,
}: {
  person: PersonInfo | null;
  createdAt: string;
}) {
  // Nome da pessoa (suporta name ou full_name)
  const personName = person?.name || person?.full_name || null;

  return (
    <div className="flex items-center justify-between py-2">
      {/* Lado esquerdo: ícone + label */}
      <div className="flex items-center gap-2">
        <UserCircle className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Criado por</span>
      </div>

      {/* Lado direito: avatar + nome + data */}
      {person ? (
        <div className="flex items-center gap-2">
          <SmartAvatar
            src={person.avatar_url}
            name={personName}
            email={person.email}
            size="xs"
            use3DFallback
          />
          <div className="text-right">
            <p className="text-sm font-medium">{personName || 'Sem nome'}</p>
            <p className="text-[10px] text-muted-foreground">{createdAt}</p>
          </div>
        </div>
      ) : (
        <span className="text-sm text-muted-foreground italic">-</span>
      )}
    </div>
  );
}

/**
 * Card individual de pessoa (para Responsável e Vendedor)
 */
function PersonCard({
  icon: Icon,
  label,
  person,
  secondaryText,
  onEdit,
}: {
  icon: React.ElementType;
  label: string;
  person: PersonInfo | null;
  secondaryText: string;
  /** Quando informado, exibe o lápis de edição (controle de permissão fica no pai) */
  onEdit?: () => void;
}) {
  // Nome da pessoa (suporta name ou full_name)
  const personName = person?.name || person?.full_name || null;

  return (
    <div className="flex items-start gap-3 py-2">
      {/* Ícone do tipo */}
      <div className="mt-0.5">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>

      {/* Conteúdo */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            {label}
          </span>
          {onEdit && (
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 text-muted-foreground hover:text-foreground shrink-0"
              onClick={onEdit}
              title={`Editar ${label.toLowerCase()}`}
            >
              <Pencil className="h-3 w-3" />
            </Button>
          )}
        </div>


        {person ? (
          <div className="flex items-center gap-2 mt-1">
            <SmartAvatar
              src={person.avatar_url}
              name={personName}
              email={person.email}
              size="xs"
              use3DFallback
            />
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">
                {personName || 'Sem nome'}
              </p>
              <p className="text-[10px] text-muted-foreground truncate">
                {secondaryText}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 mt-1">
            <div className="h-6 w-6 rounded-full bg-muted/50 flex items-center justify-center">
              <UserCircle className="h-4 w-4 text-muted-foreground/50" />
            </div>
            <p className="text-sm text-muted-foreground italic">
              Não definido
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Seção PESSOAS do modal de deal
 */
export function DealPeopleSection({
  seller,
  assignedUser,
  createdByUser,
  createdAt,
  onEditPeople,
}: DealPeopleSectionProps) {
  // Formata data de criação
  const createdAtFormatted = createdAt
    ? format(new Date(createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
    : '-';

  return (
    <div>
      {/* Header removido: SectionCard pai já renderiza o título "Pessoas" com ícone */}


      {/* 1. Criado por - Layout horizontal compacto (PRIMEIRO) */}
      <CreatedByRow person={createdByUser} createdAt={createdAtFormatted} />

      {/* 2. Responsável e Vendedor - Cards com indentação e background sutil */}
      <div className="ml-2 mt-3 space-y-3 bg-muted/30 rounded-lg p-3">
        {/* Responsável */}
        <PersonCard
          icon={UserCircle}
          label="Responsável"
          person={assignedUser}
          secondaryText="Responsável interno"
          onEdit={onEditPeople}
        />

        {/* Vendedor */}
        <PersonCard
          icon={Briefcase}
          label="Vendedor"
          person={seller}
          secondaryText="Vendedor responsável"
          onEdit={onEditPeople}
        />
      </div>
    </div>

  );
}
